"use client"

import { useState } from "react"
import { useRouter } from "@/src/hooks/useRouter"
import { DashboardLayout } from "@/components/dashboard/dashboard-layout"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { 
  UserPlus, 
  AlertCircle, 
  CheckCircle, 
  ArrowLeft, 
  Mail, 
  Phone,
  User,
  Shield,
  Info
} from "lucide-react"
import { inviteTeamMember, isValidEmail } from "@/lib/api/team"
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group"

export default function InviteTeamMemberPage() {
  const router = useRouter()
  const [email, setEmail] = useState("")
  const [fullName, setFullName] = useState("")
  const [mobile, setMobile] = useState("")
  const [roleCode, setRoleCode] = useState<"customer_admin" | "customer_user">("customer_user")
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    // Validation
    if (!email) {
      setError("กรุณาระบุอีเมล")
      return
    }

    if (!isValidEmail(email)) {
      setError("รูปแบบอีเมลไม่ถูกต้อง")
      return
    }

    if (!fullName) {
      setError("กรุณาระบุชื่อ-นามสกุล")
      return
    }

    if (!mobile) {
      setError("กรุณาระบุเบอร์โทรศัพท์")
      return
    }

    // Validate mobile number format (10 digits)
    const mobileRegex = /^0[0-9]{9}$/
    if (!mobileRegex.test(mobile)) {
      setError("รูปแบบเบอร์โทรศัพท์ไม่ถูกต้อง (ต้องเป็นเลข 10 หลัก เริ่มต้นด้วย 0)")
      return
    }

    try {
      setLoading(true)
      setError(null)
      
      await inviteTeamMember({
        email,
        fullName,
        mobile,
        roleCode,
      })

      setSuccess(true)
    } catch (err: any) {
      console.error('Failed to invite member:', err)
      setError(err.response?.data?.error || 'ไม่สามารถส่งคำเชิญได้')
    } finally {
      setLoading(false)
    }
  }

  const handleBackToTeam = () => {
    router.push("/dashboard/team")
  }

  const handleInviteAnother = () => {
    setEmail("")
    setFullName("")
    setMobile("")
    setRoleCode("customer_user")
    setError(null)
    setSuccess(false)
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              onClick={handleBackToTeam}
              className="h-10 w-10 p-0"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <h1 className="text-2xl font-bold text-gray-900">เชิญสมาชิกใหม่</h1>
          </div>
        </div>

        {/* Success State */}
        {success ? (
          <div className="bg-white rounded-lg shadow-sm p-8">
            <div className="text-center space-y-4 max-w-md mx-auto">
              <div className="flex justify-center">
                <div className="p-4 bg-green-100 rounded-full">
                  <CheckCircle className="h-12 w-12 text-green-600" />
                </div>
              </div>
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  ส่งคำเชิญสำเร็จ!
                </h3>
                <p className="text-gray-600 mb-1">
                  อีเมลคำเชิญได้ถูกส่งไปยัง
                </p>
                <p className="font-semibold text-gray-900">{email}</p>
              </div>
              <Alert className="bg-blue-50 border-blue-200 text-left">
                <Info className="h-4 w-4 text-blue-600" />
                <AlertDescription className="text-blue-800">
                  ผู้รับคำเชิญจะได้รับลิงก์เพื่อสร้างบัญชีและเข้าร่วมทีม 
                  ลิงก์จะหมดอายุใน 7 วัน
                </AlertDescription>
              </Alert>
              <div className="flex gap-3 justify-center pt-4">
                <Button
                  variant="outline"
                  onClick={handleBackToTeam}
                  className="min-w-[140px]"
                >
                  กลับไปหน้าทีม
                </Button>
                <Button
                  onClick={handleInviteAnother}
                  className="bg-purple-600 hover:bg-purple-700 min-w-[140px]"
                >
                  <UserPlus className="h-4 w-4 mr-2" />
                  เชิญสมาชิกอีกคน
                </Button>
              </div>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-6">ข้อมูลสมาชิก</h2>
              
              <div className="space-y-5 max-w-2xl">
                {/* Full Name */}
                <div className="space-y-2">
                  <Label htmlFor="fullName" className="text-sm font-medium text-gray-700">
                    ชื่อ-นามสกุล <span className="text-red-500">*</span>
                  </Label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <Input
                      id="fullName"
                      type="text"
                      placeholder="ระบุชื่อ-นามสกุล"
                      value={fullName}
                      onChange={(e) => setFullName(e.target.value)}
                      disabled={loading}
                      className="pl-10 h-11"
                    />
                  </div>
                </div>

                {/* Mobile */}
                <div className="space-y-2">
                  <Label htmlFor="mobile" className="text-sm font-medium text-gray-700">
                    เบอร์โทรศัพท์ <span className="text-red-500">*</span>
                  </Label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <Input
                      id="mobile"
                      type="tel"
                      placeholder="0812345678"
                      value={mobile}
                      onChange={(e) => setMobile(e.target.value.replace(/[^0-9]/g, ''))}
                      disabled={loading}
                      maxLength={10}
                      className="pl-10 h-11"
                    />
                  </div>
                  <p className="text-sm text-gray-500">
                    ตัวเลข 10 หลัก เริ่มต้นด้วย 0
                  </p>
                </div>

                {/* Email */}
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-sm font-medium text-gray-700">
                    อีเมล <span className="text-red-500">*</span>
                  </Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <Input
                      id="email"
                      type="email"
                      placeholder="example@email.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      disabled={loading}
                      className="pl-10 h-11"
                    />
                  </div>
                  <p className="text-sm text-gray-500">
                    อีเมลนี้จะใช้สำหรับส่งคำเชิญและเข้าสู่ระบบ
                  </p>
                </div>

                {/* Role Selection */}
                <div className="space-y-2">
                  <Label className="text-sm font-medium text-gray-700">
                    บทบาท <span className="text-red-500">*</span>
                  </Label>
                  <RadioGroup 
                    value={roleCode} 
                    onValueChange={(value: any) => setRoleCode(value)}
                    disabled={loading}
                    className="space-y-3"
                  >
                    {/* Admin Role */}
                    <label
                      htmlFor="role-admin"
                      className={`flex items-start space-x-3 p-4 rounded-lg border-2 cursor-pointer transition-all ${
                        roleCode === 'customer_admin'
                          ? 'border-purple-600 bg-purple-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <RadioGroupItem value="customer_admin" id="role-admin" className="mt-0.5" />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <Shield className="h-4 w-4 text-purple-600 flex-shrink-0" />
                          <span className="font-semibold text-gray-900">ผู้ดูแลระบบ (Admin)</span>
                          <span className="px-2 py-0.5 bg-purple-100 text-purple-700 text-xs rounded-full font-medium">
                            สิทธิ์สูง
                          </span>
                        </div>
                        <p className="text-sm text-gray-600 mb-2">
                          จัดการทีม เชิญสมาชิก ตรวจสอบสลิป และดูรายงานทั้งหมด
                        </p>
                        <ul className="text-sm text-gray-600 space-y-1">
                          <li className="flex items-center gap-2">
                            <CheckCircle className="h-3.5 w-3.5 text-green-600 flex-shrink-0" />
                            ตรวจสอบสลิปทุกประเภท
                          </li>
                          <li className="flex items-center gap-2">
                            <CheckCircle className="h-3.5 w-3.5 text-green-600 flex-shrink-0" />
                            ดูรายงานและสถิติทั้งหมด
                          </li>
                          <li className="flex items-center gap-2">
                            <CheckCircle className="h-3.5 w-3.5 text-green-600 flex-shrink-0" />
                            เชิญและจัดการสมาชิกระดับพนักงาน
                          </li>
                          <li className="flex items-center gap-2">
                            <CheckCircle className="h-3.5 w-3.5 text-green-600 flex-shrink-0" />
                            จัดการสาขาและกำหนดสิทธิ์
                          </li>
                        </ul>
                      </div>
                    </label>

                    {/* Staff Role */}
                    <label
                      htmlFor="role-staff"
                      className={`flex items-start space-x-3 p-4 rounded-lg border-2 cursor-pointer transition-all ${
                        roleCode === 'customer_user'
                          ? 'border-purple-600 bg-purple-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <RadioGroupItem value="customer_user" id="role-staff" className="mt-0.5" />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <User className="h-4 w-4 text-gray-600 flex-shrink-0" />
                          <span className="font-semibold text-gray-900">พนักงาน (Staff)</span>
                        </div>
                        <p className="text-sm text-gray-600 mb-2">
                          ตรวจสอบสลิปและดูประวัติการตรวจสอบของตนเอง
                        </p>
                        <ul className="text-sm text-gray-600 space-y-1">
                          <li className="flex items-center gap-2">
                            <CheckCircle className="h-3.5 w-3.5 text-green-600 flex-shrink-0" />
                            ตรวจสอบสลิปตามสาขาที่ได้รับมอบหมาย
                          </li>
                          <li className="flex items-center gap-2">
                            <CheckCircle className="h-3.5 w-3.5 text-green-600 flex-shrink-0" />
                            ดูประวัติการตรวจสอบของตนเอง
                          </li>
                        </ul>
                      </div>
                    </label>
                  </RadioGroup>
                  <p className="text-sm text-gray-500">
                    Owner สามารถสร้าง Admin และ Staff ได้ / Admin สามารถสร้างเฉพาะ Staff ได้
                  </p>
                </div>

                {/* Error Alert */}
                {error && (
                  <Alert variant="destructive">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>{error}</AlertDescription>
                  </Alert>
                )}

                {/* Info Alert */}
                <Alert className="bg-blue-50 border-blue-200">
                  <Info className="h-4 w-4 text-blue-600" />
                  <AlertDescription className="text-blue-800">
                    <strong>หมายเหตุ:</strong> อีเมลเชิญจะหมดอายุใน 7 วัน หลังจากนั้นจะต้องส่งคำเชิญใหม่
                  </AlertDescription>
                </Alert>

                {/* Actions */}
                <div className="flex justify-end gap-3 pt-4 border-t">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleBackToTeam}
                    disabled={loading}
                    className="min-w-[120px]"
                  >
                    ยกเลิก
                  </Button>
                  <Button
                    type="submit"
                    className="bg-purple-600 hover:bg-purple-700 min-w-[120px]"
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        กำลังส่ง...
                      </>
                    ) : (
                      <>
                        <UserPlus className="h-4 w-4 mr-2" />
                        ส่งคำเชิญ
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </div>
          </form>
        )}
      </div>
    </DashboardLayout>
  )
}
